import { showDeleteConfirmation } from '@modules/common';
import { switchMap } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import { CommentId, CommentResponse } from '@business/entities/comment';
import { CommentAPI } from '@business/api/comment';
import { TenantIdentity } from '@business/entities/common';
import { UserResponse } from '@business/entities/account';

export class CommentBloc extends BaseBloc {
  private readonly commentRepo = new Repository<CommentResponse>({
    getItemId: comment => comment.id,
  });

  readonly comments$ = this.commentRepo.items$;
  readonly selectComment = this.commentRepo.selectItem;

  onReset() {
    this.commentRepo.reset();
  }

  fetchComments = (objectId: string, type: string, identity?: TenantIdentity, isPublic = false) => {
    return CommentAPI.fetchComments({ objectId, type }, identity, isPublic).pipe(
      this.commentRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_comments_error') }),
    );
  };

  fetchComment = (id: CommentId, identity?: TenantIdentity) => {
    return CommentAPI.fetchComment(id, identity).pipe(
      this.commentRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_comments_error') }),
    );
  };

  createComment = (comment: Partial<CommentResponse>, identity?: TenantIdentity, user?: UserResponse | null) => {
    return CommentAPI.createComment(comment, identity, user).pipe(
      this.commentRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_comment'),
      }),
    );
  };

  updateComment = (id: CommentId, company: Partial<CommentResponse>, identity?: TenantIdentity) => {
    return CommentAPI.updateComment(id, company, identity).pipe(
      this.commentRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_comment'),
      }),
    );
  };

  syncToClickup = (id: CommentId, identity?: TenantIdentity) => {
    return CommentAPI.syncToClickup(id, identity).pipe(
      this.commentRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ type: requestMessage('create_comment') }),
    );
  };

  resetComments = () => {
    this.commentRepo.reset();
  };

  deleteComment = (companyId: CommentId, identity?: TenantIdentity) => {
    return showDeleteConfirmation('Delete Comapny', 'Do you really want to remove this comment?').pipe(
      switchMap(() => CommentAPI.deleteComment(companyId, identity)),
      this.commentRepo.ops.removeOne(() => companyId),
      handleMessage({
        type: requestMessage('delete_comment'),
      }),
    );
  };
}
